import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const SUBJECTS = [
  { ar: 'شراكة استثمارية', en: 'Investment partnership' },
  { ar: 'تصدير وتطوير أعمال', en: 'Export & business development' },
  { ar: 'توريد منتجات (أعلاف · عسل · مخصبات)', en: 'Product supply (feed · honey · fertilizers)' },
  { ar: 'خدمات لوجستية ونقل', en: 'Logistics & transport services' },
  { ar: 'توظيف وفرص عمل', en: 'Careers & job opportunities' },
  { ar: 'استفسار عام', en: 'General inquiry' },
];

const EMPTY = { name: '', company: '', email: '', phone: '', subject: '', message: '' };
const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const req = { color: 'var(--gold-600)' };

export default function ContactPage() {
  const { isEN, t } = useLanguage();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.title = isEN ? 'Contact Us — Elhaware Group' : 'اتصل بنا — مجموعة الهواري';
  }, [isEN]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(false), 6000);
    return () => clearTimeout(t);
  }, [success]);

  const set = (k) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [k]: value }));
    setErrors((er) => {
      if (!er[k]) return er;
      const next = { ...er };
      delete next[k];
      return next;
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const er = {};
    if (!values.name.trim()) er.name = true;
    if (!values.email.trim() || !validEmail(values.email.trim())) er.email = true;
    if (!values.subject) er.subject = true;
    if (!values.message.trim()) er.message = true;
    setErrors(er);
    if (Object.keys(er).length) return;
    setSuccess(true);
    setValues(EMPTY);
  };

  const fieldCls = (k) => 'field' + (errors[k] ? ' invalid' : '');

  return (
    <>
      <section className="page-hero" style={{ paddingBlock: '180px 70px' }}>
        <div className="page-hero__bg">
          <div className="ph shot" data-parallax="0.08" role="img" aria-label={isEN ? 'Group facilities' : 'منشآت المجموعة'} style={{ backgroundImage: "url('/assets/img/ceo-plant.webp')" }}></div>
        </div>
        <div className="page-hero__glow"></div>
        <div className="wrap">
          <nav className="breadcrumb" data-reveal="fade"><Link to="/">{isEN ? 'Home' : 'الرئيسية'}</Link><span>/</span><span style={{ color: 'rgba(255,255,255,.85)' }}>{isEN ? 'Contact Us' : 'اتصل بنا'}</span></nav>
          <span className="eyebrow" data-reveal>{isEN ? 'Contact Us' : 'تواصل معنا'}</span>
          <h1 data-reveal data-delay="1">{isEN ? <>Let's start a <b>conversation</b></> : <>لنبدأ <b>حواراً</b></>}</h1>
          <p className="page-hero__lead" data-reveal data-delay="2">{isEN ? 'We welcome strategic partnerships and opportunities for collaboration and export across all of the Group\'s sectors. Our team is ready to answer your inquiries.' : 'نرحّب بالشراكات الاستراتيجية وفرص التعاون والتصدير في كل قطاعات المجموعة. فريقنا جاهز للرد على استفساراتكم.'}</p>
        </div>
      </section>

      <section className="section">
        <div className="wrap contact-grid">
          <div data-reveal="right">
            <div className="shead">
              <span className="eyebrow">{isEN ? 'Contact Information' : 'معلومات التواصل'}</span>
              <h2 style={{ fontSize: 'clamp(26px,3.2vw,40px)' }}>{isEN ? <>We're glad to <b>hear from you</b></> : <>نسعد <b>بتواصلكم</b></>}</h2>
            </div>
            <div className="contact-info">
              <a className="cinfo" href={`https://wa.me/201222113013?text=${encodeURIComponent(isEN ? 'Hello Elhaware Group, I would like to know more about your products and services.' : 'مرحباً مجموعة الهواري، أودّ معرفة المزيد عن منتجاتكم وخدماتكم.')}`} target="_blank" rel="noopener noreferrer">
                <span className="cinfo__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg></span>
                <div><div className="cinfo__t">{isEN ? 'Phone' : 'الهاتف'}</div><div className="cinfo__v" dir="ltr">+20 122 211 3013</div></div>
              </a>
              <div className="cinfo">
                <span className="cinfo__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg></span>
                <div>
                  <div className="cinfo__t">{isEN ? 'Email' : 'البريد الإلكتروني'}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                    <a className="cinfo__v cinfo__link" dir="ltr" href="mailto:abeermomtaz@elhawaregroup.com" style={{ display: 'inline-block' }}>abeermomtaz@elhawaregroup.com</a>
                    <a className="cinfo__v cinfo__link" dir="ltr" href="mailto:info@elhawaregroup.com" style={{ display: 'inline-block' }}>info@elhawaregroup.com</a>
                  </div>
                </div>
              </div>
              <div className="cinfo">
                <span className="cinfo__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg></span>
                <div><div className="cinfo__t">{isEN ? 'Headquarters' : 'المقر الإداري'}</div><div className="cinfo__v">{isEN ? '13 Al Ahly Bank Building · Sphinx Square · 8th Floor · Mohandeseen · Giza · Egypt' : '13 مبنى البنك الأهلي · ميدان سفنكس · الدور الثامن · المهندسين · الجيزة · مصر'}</div></div>
              </div>
              <div className="cinfo">
                <span className="cinfo__ic"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg></span>
                <div><div className="cinfo__t">{isEN ? 'Working Hours' : 'ساعات العمل'}</div><div className="cinfo__v">{isEN ? 'Saturday – Thursday · 9:00 AM – 5:00 PM' : 'السبت – الخميس · 9:00 ص – 5:00 م'}</div></div>
              </div>
            </div>
          </div>

          <div className="form-card" data-reveal="left">
            <div className={'form-success' + (success ? ' show' : '')}>{isEN ? 'Thank you for reaching out — your message has been received and our team will get back to you shortly.' : 'شكراً لتواصلكم — تم استلام رسالتكم وسيعاود فريقنا الاتصال بكم قريباً.'}</div>
            <form noValidate onSubmit={onSubmit}>
              <div className="form-row">
                <div className={fieldCls('name')}>
                  <label htmlFor="name">{isEN ? 'Name' : 'الاسم'}<span style={req}> *</span></label>
                  <input type="text" id="name" name="name" placeholder={isEN ? 'Full name' : 'الاسم بالكامل'} value={values.name} onChange={set('name')} />
                  <div className="field__err">{isEN ? 'Please enter your name.' : 'يرجى إدخال الاسم.'}</div>
                </div>
                <div className="field">
                  <label htmlFor="company">{isEN ? 'Organization / Company' : 'جهة العمل / الشركة'}</label>
                  <input type="text" id="company" name="company" placeholder={isEN ? 'Company name (optional)' : 'اسم الشركة (اختياري)'} value={values.company} onChange={set('company')} />
                </div>
              </div>
              <div className="form-row">
                <div className={fieldCls('email')}>
                  <label htmlFor="email">{isEN ? 'Email' : 'البريد الإلكتروني'}<span style={req}> *</span></label>
                  <input type="email" id="email" name="email" placeholder="your name @ company.com" dir="ltr" style={{ textAlign: 'start' }} value={values.email} onChange={set('email')} />
                  <div className="field__err">{isEN ? 'Please enter a valid email address.' : 'يرجى إدخال بريد إلكتروني صحيح.'}</div>
                </div>
                <div className="field">
                  <label htmlFor="phone">{isEN ? 'Phone' : 'رقم الهاتف'}</label>
                  <input type="tel" id="phone" name="phone" placeholder="+20 ..." dir="ltr" style={{ textAlign: 'start' }} value={values.phone} onChange={set('phone')} />
                </div>
              </div>
              <div className={fieldCls('subject')}>
                <label htmlFor="subject">{isEN ? 'Subject of Inquiry' : 'موضوع الاستفسار'}<span style={req}> *</span></label>
                <select id="subject" name="subject" value={values.subject} onChange={set('subject')}>
                  <option value="">{isEN ? 'Choose a subject…' : 'اختر الموضوع…'}</option>
                  {SUBJECTS.map((s) => <option key={s.ar} value={t(s)}>{t(s)}</option>)}
                </select>
                <div className="field__err">{isEN ? 'Please select a subject.' : 'يرجى اختيار موضوع.'}</div>
              </div>
              <div className={fieldCls('message')}>
                <label htmlFor="message">{isEN ? 'Message' : 'الرسالة'}<span style={req}> *</span></label>
                <textarea id="message" name="message" placeholder={isEN ? 'Write the details of your inquiry here…' : 'اكتب تفاصيل استفسارك هنا…'} value={values.message} onChange={set('message')}></textarea>
                <div className="field__err">{isEN ? 'Please write your message.' : 'يرجى كتابة رسالتك.'}</div>
              </div>
              <button type="submit" className="btn btn-gold" style={{ width: '100%', justifyContent: 'center' }}><span>{isEN ? 'Send Message' : 'إرسال الرسالة'}</span> <span className="arr">←</span></button>
            </form>
          </div>
        </div>
      </section>

      <section className="map-band section" style={{ paddingBlock: '0 0' }}>
        <div className="wrap" style={{ paddingBottom: 'clamp(64px,9vw,120px)' }}>
          <div className="shead center" data-reveal style={{ marginBottom: 40 }}>
            <span className="eyebrow">{isEN ? 'Where We Operate' : 'أين نعمل'}</span>
            <h2 style={{ fontSize: 'clamp(26px,3.2vw,40px)' }}>{isEN ? <>A <b>local and global</b> presence</> : <>حضورٌ <b>محلي ودولي</b></>}</h2>
          </div>
          <div className="map-band__frame" data-reveal="scale">
            <iframe title={isEN ? 'Headquarters map · Mohandeseen, Giza' : 'خريطة المقر · المهندسين، الجيزة'} src="https://maps.google.com/maps?q=Sphinx%20Square%2C%20Mohandeseen%2C%20Giza%2C%20Egypt&z=15&output=embed" style={{ width: '100%', height: '100%', border: 0, display: 'block', filter: 'grayscale(.2) contrast(1.05)' }} loading="lazy" referrerPolicy="no-referrer-when-downgrade"></iframe>
          </div>
        </div>
      </section>
    </>
  );
}
